package com.steamstreet.vegasful.html.components

import com.steamstreet.vegasful.css.css
import kotlinx.css.fontSize
import kotlinx.css.px
import kotlinx.html.FlowContent
import kotlinx.html.span
import kotlinx.html.unsafe

/**
 * Icon styles. Retained for source compatibility with existing call sites; the concrete glyph is
 * now selected purely by id from the inline-SVG registry below, so the style is no longer used at
 * render time.
 */
enum class IconType {
    Solid,
    Regular,
    Light,
    Duotone,
    Brand
}

/**
 * Whether an icon is drawn with outlined strokes (Lucide style) or solid fills (Simple Icons /
 * solid style). This controls the root `<svg>` presentation attributes so each icon's path data
 * inherits the right paint.
 */
private enum class IconStyle { Stroke, Fill }

private class IconDef(val viewBox: String, val style: IconStyle, val body: String)

/**
 * Self-hosted inline-SVG icon registry. This replaces the FontAwesome CDN kit: icons ship directly
 * in the rendered HTML, so there are no external requests, no usage limits, and no cost.
 *
 * UI glyphs are sourced from Lucide (ISC license) and brand glyphs from Simple Icons (CC0). Both
 * use a 24x24 viewBox unless noted. Keys match the names that were previously passed to
 * FontAwesome's `fa-` classes, so call sites are unchanged.
 *
 * **This registry lives in `common/html` rather than in the server's page module because it has to
 * serve both rendering paths.** Server-rendered pages and the browser client both draw icons, and
 * when each had its own copy the two drifted: an icon added for one path silently rendered nothing
 * on the other.
 */
private val icons: Map<String, IconDef> = buildMap {
    fun stroke(vararg names: String, body: String, viewBox: String = "0 0 24 24") {
        val def = IconDef(viewBox, IconStyle.Stroke, body)
        names.forEach { put(it, def) }
    }

    fun fill(vararg names: String, body: String, viewBox: String = "0 0 24 24") {
        val def = IconDef(viewBox, IconStyle.Fill, body)
        names.forEach { put(it, def) }
    }

    // --- UI icons (Lucide, outlined) ---
    stroke("angle-left", "chevron-left", body = """<path d="m15 18-6-6 6-6"/>""")
    stroke("angle-right", "chevron-right", body = """<path d="m9 18 6-6-6-6"/>""")
    stroke("caret-down", "chevron-down", body = """<path d="m6 9 6 6 6-6"/>""")
    stroke("bars", body = """<path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/>""")
    stroke("xmark", "x", body = """<path d="M18 6 6 18"/><path d="m6 6 12 12"/>""")
    stroke("check", body = """<path d="M20 6 9 17l-5-5"/>""")
    stroke(
        "search",
        body = """<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>"""
    )
    stroke(
        "calendar",
        body = """<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/>"""
    )
    stroke(
        "calendar-plus",
        body = """<path d="M8 2v4"/><path d="M16 2v4"/><path d="M21 13V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7"/><path d="M3 10h18"/><path d="M16 19h6"/><path d="M19 16v6"/>"""
    )
    stroke(
        "map",
        body = """<polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" x2="9" y1="3" y2="18"/><line x1="15" x2="15" y1="6" y2="21"/>"""
    )
    stroke(
        "phone",
        body = """<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>"""
    )
    stroke(
        "globe",
        body = """<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>"""
    )
    stroke(
        "ticket",
        body = """<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 11v2"/><path d="M13 17v2"/>"""
    )
    stroke(
        "share",
        body = """<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>"""
    )

    // --- Filled glyphs ---
    // Heart uses a solid fill so the "liked" state simply recolors it, matching
    // the previous FontAwesome solid heart.
    fill(
        "heart",
        viewBox = "0 0 24 24",
        body = """<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>"""
    )

    // --- Brand icons (Simple Icons, filled) ---
    fill(
        "facebook",
        body = """<path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/>"""
    )
    // "twitter" now renders the current X brand mark.
    fill(
        "twitter",
        body = """<path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>"""
    )
    fill(
        "instagram",
        body = """<path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0Zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227a3.81 3.81 0 0 1-.899 1.382 3.744 3.744 0 0 1-1.38.896c-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421a3.716 3.716 0 0 1-1.379-.899 3.644 3.644 0 0 1-.9-1.38c-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03Zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 1 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm7.846-10.405a1.441 1.441 0 0 1-2.88 0 1.44 1.44 0 0 1 2.88 0Z"/>"""
    )
    fill(
        "pinterest",
        body = """<path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345c-.091.378-.293 1.194-.333 1.361-.052.22-.174.266-.401.16-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12Z"/>"""
    )
}

/**
 * Builds the raw inline-SVG markup for [id]. Shared by the server renderer in [icon] and exposed so
 * the browser client can inject the exact same glyph.
 *
 * The SVG is sized to `1em` and painted with `currentColor`, so existing CSS that sets `font-size`
 * and `color` on the surrounding element keeps working exactly as it did with the FontAwesome font
 * glyphs. Returns an empty string for an unknown id.
 */
fun iconSvg(id: String): String {
    val def = icons[id] ?: return ""
    val presentation = when (def.style) {
        IconStyle.Stroke ->
            """fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round""""
        IconStyle.Fill -> """fill="currentColor""""
    }
    return """<svg xmlns="http://www.w3.org/2000/svg" viewBox="${def.viewBox}" $presentation """ +
            """width="1em" height="1em" aria-hidden="true" focusable="false" """ +
            """style="display:inline-block;vertical-align:-0.125em">${def.body}</svg>"""
}

/**
 * Render the icon named [id] as inline SVG.
 *
 * **There is deliberately only one of these in the codebase.** There used to be a second `icon()`
 * with the same signature — this one, emitting FontAwesome's `<i class="fas fa-…">` — and which of
 * the two a call site got depended on nothing more visible than which package it had imported.
 * FontAwesome was removed over licensing, so the call sites that happened to pick the legacy one
 * drew nothing at all and said nothing about it.
 */
fun FlowContent.icon(id: String, type: IconType = IconType.Solid) {
    span("icon icon-$id") {
        unsafe { +iconSvg(id) }
    }
}

/** Marks the spinner's wrapper, so a caller can show and hide it without reaching for a tag name. */
const val SPINNER_CLASS = "vegasful-spinner"

/**
 * A spinning ring, sized to the text it sits in and coloured by it.
 *
 * The markup is shared with the client-side registry in `web/public`, so there is one spinner in
 * the product rather than one per rendering path.
 *
 * **Animated with SMIL rather than CSS keyframes**, following the choice already made when Font
 * Awesome was removed: the SVG carries its own animation, so it needs no companion stylesheet and
 * works wherever the markup lands. Keyframes would also have had nowhere to live — the `css {}`
 * builder cannot express them, since `CssManager.writeStyleSheet` treats every `@` rule as a media
 * query and nests the element's selector inside it.
 */
const val SPINNER_SVG: String =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" """ +
            """stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="1em" height="1em" """ +
            """aria-hidden="true" focusable="false" style="display:inline-block;vertical-align:-0.125em">""" +
            """<path d="M21 12a9 9 0 1 1-6.219-8.56">""" +
            """<animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" """ +
            """dur="0.8s" repeatCount="indefinite"/>""" +
            """</path></svg>"""

/**
 * Render the spinner.
 *
 * This was `icon("spinner", spin = SpinType.Spin)`, which emits Font Awesome's classes — and Font
 * Awesome was removed from the site over licensing. It rendered an empty `<i>`, so every spinner
 * built on it was invisible and silent about it, including the one My Guide shows while loading.
 *
 * Sized to `1em` and painted with `currentColor`, so surrounding `font-size` and `color` control it
 * exactly as the glyph did — which is what lets [largeSpinner] keep working by setting a font size
 * and nothing else.
 *
 * **It does not hide itself.** A spinner is shown for part of a component's life and not the rest,
 * and only the component knows when, so the caller owns its visibility.
 */
fun FlowContent.spinner() {
    span(classes = SPINNER_CLASS) {
        unsafe { +SPINNER_SVG }
    }
}

fun FlowContent.largeSpinner() {
    span {
        css {
            fontSize = 36.px
        }
        spinner()
    }
}
